import { useCallback, useContext, useEffect, useState } from "react";
import { useAccess } from "../../../access/hooks/useAccess";
import { billingAllows } from "../billingAccess";
import { Context } from "../../../context/Context";
import { Button, SectionHeader } from "../../ui";
import { getBillingAccessAPI } from "../../../utils/api/billing";
import { useBillingAdminText } from "./billingAdminI18n";
import InvoicesTab from "./components/InvoicesTab";
import SubscriptionsTab from "./components/SubscriptionsTab";
import FollowUpTab from "./components/FollowUpTab";
import SettingsTab from "./components/SettingsTab";
import AiWalletsTab from "./components/AiWalletsTab";
import AccountantView from "../accountant/AccountantView";
import { usePageSection } from "../../../navigation/pageSections";
import IssueInvoiceModal from "./modals/IssueInvoiceModal";
import "./BillingAdmin.css";

const BillingAdmin = ({ onViewAsStore }) => {
  const token = localStorage.getItem("auth-token");
  const { lang = "en" } = useContext(Context) || {};
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  // Sections are opened from the sidebar submenu (`?tab=`).
  const [activeTab, setTab] = usePageSection("billing");
  const [access, setAccess] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [issueModalOpen, setIssueModalOpen] = useState(false);

  const canIssueInvoice = billingAllows(can("billing.admin.invoice.issue"), access?.canIssueInvoice);

  const loadAccess = useCallback(async () => {
    if (!token) return;
    try {
      const result = await getBillingAccessAPI(token);
      setAccess(result.data);
    } catch {
      // access defaults to role-based fallbacks (useAccess) below
    }
  }, [token]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const refresh = async () => {
    setRefreshing(true);
    await loadAccess();
    setRefreshKey((key) => key + 1);
    setRefreshing(false);
  };

  return (
    <main className="billing-admin billing-admin-v141" dir={lang === "ar" ? "rtl" : "ltr"}>
      <SectionHeader
        title={text.shell.title}
        description={text.shell.description}
        actions={
          <div className="billing-admin-header-actions">
            <Button appearance="outline" onClick={refresh} loading={refreshing}>
              {text.shell.refresh}
            </Button>
            {onViewAsStore && can("billing.viewAsStore") && (
              <Button appearance="outline" onClick={onViewAsStore}>
                {lang === "ar" ? "عرض كمتجر" : "View as Store"}
              </Button>
            )}
            {canIssueInvoice && (
              <Button appearance="filled" onClick={() => setIssueModalOpen(true)}>
                {text.shell.issueInvoice}
              </Button>
            )}
          </div>
        }
      />
      <div className="billing-admin-panel">
        {activeTab === "invoices" && (
          <InvoicesTab key={`invoices-${refreshKey}`} access={access} />
        )}
        {activeTab === "subscriptions" && (
          <SubscriptionsTab key={`subscriptions-${refreshKey}`} access={access} />
        )}
        {activeTab === "followUp" && (
          <FollowUpTab key={`followUp-${refreshKey}`} access={access} />
        )}
        {activeTab === "accountant" && (
          <AccountantView key={`accountant-${refreshKey}`} lang={lang} embedded />
        )}
        {activeTab === "aiWallets" && (
          <AiWalletsTab key={`aiWallets-${refreshKey}`} access={access} onReviewPayments={() => setTab("invoices")} />
        )}
        {activeTab === "settings" && (
          <SettingsTab key={`settings-${refreshKey}`} access={access} />
        )}
      </div>

      {issueModalOpen && (
        <IssueInvoiceModal
          onCancel={() => setIssueModalOpen(false)}
          onIssued={() => {
            setIssueModalOpen(false);
            setRefreshKey((key) => key + 1);
          }}
        />
      )}
    </main>
  );
};

export default BillingAdmin;
